import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const article = getArticleById(id || '');

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">该文章可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-all duration-200 no-underline"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <div className="bg-background-100 border-b border-background-200">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="inline-block px-2.5 py-1 text-xs font-medium bg-primary-500 text-background-50 rounded-md">
                {article.categoryName}
              </span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block px-2.5 py-1 text-xs font-medium bg-secondary-100 text-secondary-700 rounded-md"
                >
                  {tag}
                </span>
              ))}
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读时间 {article.readTime}
              </span>
            </div>
          </div>

          <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden mb-10">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={`${article.title} - 赤裸特工`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: article.content
                .replace(/<p>/g, '<p class="text-base mb-4 leading-relaxed">')
                .replace(/<strong>/g, '<strong class="font-semibold text-foreground-900">')
                .replace(/<br\/>/g, '<br/>'),
            }}
          />

          {relatedArticles.length > 0 && (
            <div className="mt-16 pt-10 border-t border-background-200">
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.id} article={related} />
                ))}
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            datePublished: article.date,
            url: `https://www.chiluotegong.com/article/${article.id}`,
            author: {
              '@type': 'Organization',
              name: '赤裸特工官方',
            },
            publisher: {
              '@type': 'Organization',
              name: '赤裸特工官方',
            },
            about: {
              '@type': 'TVSeries',
              name: '赤裸特工',
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '首页',
                item: 'https://www.chiluotegong.com/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: article.categoryName,
                item: `https://www.chiluotegong.com/category/${article.category}`,
              },
              {
                '@type': 'ListItem',
                position: 3,
                name: article.title,
              },
            ],
          }),
        }}
      />
    </>
  );
}